import type { MessageKey } from '@ralph/contracts'
import { formatBytes } from '@/i18n/format.ts'
import type { ServerMessages } from '@/i18n/messages/en/errors/index.ts'

/**
 * What the artifacts domain's refusals say, in English (the source of truth AND the type).
 *
 * The namespace is selected from `MessageKey` rather than listed by hand: a key added to
 * `ArtifactsPayloads` becomes a missing property HERE, in the file that owes it a sentence,
 * instead of an error at the aggregator that names no domain.
 *
 * ── Who reads these ──
 *
 * Not an operator. These arrive in the conversation column, under an answer, when somebody
 * clicks a file a run just produced — so they say what happened to that file and stop. The
 * content check's magic-byte diagnostic and the exact rounded megabyte figure both stay in
 * the server's English `message`, where the person who needs them is reading a log.
 */
type ArtifactsMessages = Pick<ServerMessages, Extract<MessageKey, `artifacts.${string}`>>

/**
 * This file's own locale, named once.
 *
 * A catalogue entry takes its parameters and nothing else — `describeApiError` looks a key up
 * and calls it, with no formatter bound — so an entry that sizes something has to name the
 * locale it IS. That is not the hardcoded 'en' LOC-020 bans: it is this file's identity, and
 * the pt-BR sibling says 'pt-BR' in the same place for the same reason.
 */
const LOCALE = 'en'

export const artifacts: ArtifactsMessages = {
  // --- producing one --------------------------------------------------------
  'artifacts.needs_a_name': () => 'An artifact needs a name.',
  'artifacts.too_large': ({ name, sizeBytes, limitBytes }) =>
    `“${name}” is ${formatBytes(LOCALE, sizeBytes)}, and an artifact can be up to ` +
    `${formatBytes(LOCALE, limitBytes)}.`,

  // --- reading one ----------------------------------------------------------
  //
  // The first sentence names nothing on purpose: it answers "there is no such artifact" and
  // "that one is not yours" identically, so the endpoint is not an existence oracle.
  'artifacts.not_available': () => 'No artifact with that id is available to you.',
  'artifacts.refused_by_content_check': ({ name }) =>
    `“${name}” was refused by the content check and cannot be downloaded.`,
  'artifacts.not_checked_yet': ({ name }) => `“${name}” has not been checked yet and cannot be downloaded.`
}
